import { ReactElement, useEffect, useState } from "react";
import {
  FEATURE_FLAG_SESSION_STORAGE_EVENT,
  FEATURE_FLAG_SESSION_STORAGE_KEY,
  FEATURE_FLAG_SESSION_STORAGE_LOCKED_KEY,
} from "./feature-flag.constants";
import { FeatureFlagContext } from "./feature-flag.context";

type FeatureFlagSessionStorageProviderProps = { apiUrl: string; children: ReactElement | ReactElement[] };
export const FeatureFlagSessionStorageProvider = ({
  apiUrl = "",
  children,
}: FeatureFlagSessionStorageProviderProps) => {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [hasLoadedFlags, setHasLoadedFlags] = useState(false);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem(FEATURE_FLAG_SESSION_STORAGE_KEY);
      sessionStorage.removeItem(FEATURE_FLAG_SESSION_STORAGE_LOCKED_KEY);
    };
    addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!apiUrl || hasLoadedFlags) {
      return;
    }
    if (sessionStorage.getItem(FEATURE_FLAG_SESSION_STORAGE_KEY)) {
      setFlags(JSON.parse(sessionStorage.getItem(FEATURE_FLAG_SESSION_STORAGE_KEY) || "{}"));
      setHasLoadedFlags(true);
      return;
    }
    if (!sessionStorage.getItem(FEATURE_FLAG_SESSION_STORAGE_LOCKED_KEY)) {
      sessionStorage.setItem(FEATURE_FLAG_SESSION_STORAGE_LOCKED_KEY, "true");
      (async () => {
        try {
          const response = await fetch(apiUrl, { headers: { "Content-Type": "application/json" }, method: "GET" });
          if (!response.ok) {
            throw new Error("Failed to fetch feature flags");
          }
          const data: { flags: Record<string, boolean> } = await response.json();
          if (!data?.flags || Object.keys(data.flags).length === 0) {
            throw new Error("No flags in request");
          }
          setFlags(data.flags);
          sessionStorage.setItem(FEATURE_FLAG_SESSION_STORAGE_KEY, JSON.stringify(data.flags));
        } catch {
          /* ignored */
        } finally {
          setHasLoadedFlags(true);
          sessionStorage.removeItem(FEATURE_FLAG_SESSION_STORAGE_LOCKED_KEY);
          window.dispatchEvent(new Event(FEATURE_FLAG_SESSION_STORAGE_EVENT));
        }
      })();
      return;
    }
    const storageCallback = () => {
      setFlags(JSON.parse(sessionStorage.getItem(FEATURE_FLAG_SESSION_STORAGE_KEY) || "{}"));
      setHasLoadedFlags(true);
      window.removeEventListener(FEATURE_FLAG_SESSION_STORAGE_EVENT, storageCallback);
    };
    window.addEventListener(FEATURE_FLAG_SESSION_STORAGE_EVENT, storageCallback);
    return () => {
      window.removeEventListener(FEATURE_FLAG_SESSION_STORAGE_EVENT, storageCallback);
    };
  }, [apiUrl, hasLoadedFlags]);

  return <FeatureFlagContext.Provider value={{ flags, hasLoadedFlags }}>{children}</FeatureFlagContext.Provider>;
};
